/**
 * The review model for one submission: a row per answer (group entries
 * expanded) in the client's step order, the per-answer decisions and notes,
 * what changed since the previous revision and which decisions carry forward,
 * the AI check's upload warnings, where each answer saves, the visible
 * autosave state, and the revision request. Accepting and filing lives in the
 * Finish sheet; the pane owns layout.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeAnswer,
  OutgoingIntakeFile,
  OutgoingIntakeFormNode,
  OutgoingIntakeReviewDecision,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import type { ReviewCommentThreadItem } from "@/pages/PublicOutgoingIntake/PublicOutgoingIntake";
import { flattenIntakeStepNodes } from "@/features/foreignNationalOutgoingIntakes/intakeStepFlattening";
import { isNodeVisible } from "@/features/foreignNationalOutgoingIntakes/conditional";
import {
  contextualGroupQuestionTitle,
  formatDocumentRequestTitle,
  formatGroupAnswerValue,
  formatInformationRequestTitle,
  groupChildQuestions,
  groupQuestionTitle
} from "@/features/foreignNationalOutgoingIntakes/utils/intakeReviewPhrasing";
import { normalizeAvatarSrc } from "@/helpers/userDisplay";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { toastEmailFailed } from "../linkToasts";
import { asDate } from "../model";
import { type SavesTo, savesToOf, useSaveTargets } from "../saveTargets";
import { updateNode } from "./stepModel";
import {
  type Decision,
  type DecisionState,
  type ReviewFilter,
  REVIEW_FILTERS,
  answersByKey,
  attentionReason,
  changedSince,
  decisionKey,
  draftRevisionNote,
  matchesFilter,
  safeToAccept,
  seedDecisions
} from "./reviewModel";

export type { Decision, ReviewFilter } from "./reviewModel";
export type StepDecision = Decision | "partial";
export type SaveState = "idle" | "saving" | "saved" | "error";

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

const isCustom = (n: OutgoingIntakeFormNode | null | undefined) =>
  text(n?.template_source) === "custom";

/** Phrase a question label exactly as the client saw it in the flow. */
export const reviewLabelFor = (
  q: OutgoingIntakeFormNode,
  group?: OutgoingIntakeFormNode,
  entryAnswers?: Record<string, OutgoingIntakeAnswer>
): string => {
  const base = text(q.title) || text(q.prompt);
  if (isCustom(q)) return base || "Field";
  if (group && entryAnswers) {
    const contextual = contextualGroupQuestionTitle(group, q, entryAnswers);
    if (contextual) return contextual;
  }
  if (q.question_type === "file")
    return formatDocumentRequestTitle(base) || base || "Document";
  return formatInformationRequestTitle(base) || base || "Field";
};

export const firstName = (name: string | null | undefined): string | null =>
  text(name).split(/\s+/)[0] || null;

const shortTime = (at: unknown): string | undefined => {
  const d = asDate(at);
  if (!d) return undefined;
  return `${d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  })} · ${d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  })}`;
};

/** One answer in the review table. */
export type ReviewRow = {
  key: string;
  /** The client's step number this answer belongs to. */
  step: number;
  stepNodeId: string;
  instanceId: string | null;
  node: OutgoingIntakeFormNode;
  label: string;
  /** "Employment 2" for group entries. */
  context: string | null;
  answer: OutgoingIntakeAnswer | null;
  display: string;
  files: OutgoingIntakeFile[];
  comment: string | null;
  changed: boolean;
  previousDisplay: string | null;
  previousFiles: OutgoingIntakeFile[];
  carried: boolean;
  attention: string | null;
  savesTo: SavesTo;
  decision: Decision;
  note: string;
};

export type ReviewStep = {
  node: OutgoingIntakeFormNode;
  number: number;
  label: string;
  keys: string[];
  decision: StepDecision;
  entries: {
    instanceId: string;
    label: string;
    keys: string[];
    decision: StepDecision;
  }[];
};

export const useSubmissionReview = (
  intakeId: string,
  submissionId: string,
  clientName: string | null
) => {
  const { clientId, invalidate, open } = useRequestsIde();
  const data = useRequestsData();
  const reviewerName = useSelector(lawyerSelectors.selectFullName);
  const reviewerAvatar = useSelector(lawyerSelectors.selectAvatarSrc);
  const targets = useSaveTargets();

  const detail = data.detail(intakeId);
  useEffect(() => {
    if (!detail) void data.loadDetail(intakeId);
  }, [data, detail, intakeId]);
  // A link to a submission newer than the cached detail (the client just
  // submitted): fetch once more rather than show nothing.
  const refetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!detail || refetchedFor.current === submissionId) return;
    const known = detail.submissions.some(
      (s) => s.outgoing_intake_submission_id === submissionId
    );
    if (known) return;
    refetchedFor.current = submissionId;
    void data.loadDetail(intakeId, { force: true });
  }, [data, detail, intakeId, submissionId]);
  const intake: OutgoingIntake | null = detail?.intake ?? null;
  const submissions = useMemo(
    () =>
      (detail?.submissions ?? []).slice().sort((a, b) => {
        const rev = (a.revision_number ?? 0) - (b.revision_number ?? 0);
        if (rev !== 0) return rev;
        return (
          (asDate(a.submitted_at)?.getTime() ?? 0) -
          (asDate(b.submitted_at)?.getTime() ?? 0)
        );
      }),
    [detail?.submissions]
  );
  const index = submissions.findIndex(
    (s) => s.outgoing_intake_submission_id === submissionId
  );
  const submission: OutgoingIntakeSubmission | null =
    index >= 0 ? submissions[index] : null;
  const submissionNumber = Math.max(1, index + 1);
  const previous: OutgoingIntakeSubmission | null = useMemo(() => {
    if (!submission) return null;
    const prevId = text(submission.previous_submission_id);
    if (prevId)
      return (
        submissions.find((s) => s.outgoing_intake_submission_id === prevId) ??
        null
      );
    return index > 0 ? submissions[index - 1] : null;
  }, [index, submission, submissions]);
  const latest = submissions[submissions.length - 1] ?? null;
  const superseded =
    Boolean(latest) && latest?.outgoing_intake_submission_id !== submissionId;

  const schema = useMemo(() => intake?.schema ?? [], [intake?.schema]);
  const accepted =
    intake?.status === "accepted" || submission?.review_status === "accepted";
  const locked =
    !submission ||
    superseded ||
    accepted ||
    submission.review_status === "revision_requested" ||
    intake?.status === "revision_requested";

  // ---- answers --------------------------------------------------------------
  const answerByKey = useMemo(() => answersByKey(submission), [submission]);
  const previousByKey = useMemo(() => answersByKey(previous), [previous]);
  const changed = useMemo(
    () => (submission ? changedSince(submission, previous) : null),
    [previous, submission]
  );
  const instanceOrder = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const a of submission?.answers ?? []) {
      const g = text(a.repeatable_group_id);
      const i = text(a.repeatable_group_instance_id);
      if (g && i) {
        const list = m.get(g) ?? [];
        if (!list.includes(i)) m.set(g, [...list, i]);
      }
    }
    return m;
  }, [submission]);
  const valueByQuestionId = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const a of submission?.answers ?? [])
      if (!text(a.repeatable_group_id)) out[text(a.question_id)] = a.value;
    return out;
  }, [submission]);

  /** The client's steps, minus conditional ones her answers hid. */
  const stepNodes = useMemo(
    () =>
      flattenIntakeStepNodes(schema).filter((n) =>
        isNodeVisible(n, valueByQuestionId)
      ),
    [schema, valueByQuestionId]
  );

  const entryAnswersFor = useCallback(
    (group: OutgoingIntakeFormNode, instanceId: string) => {
      const out: Record<string, OutgoingIntakeAnswer> = {};
      for (const q of groupChildQuestions(group)) {
        const a = answerByKey.get(
          decisionKey({
            question_id: q.node_id,
            repeatable_group_id: group.node_id,
            repeatable_group_instance_id: instanceId
          })
        );
        if (a) out[String(q.node_id)] = a;
      }
      return out;
    },
    [answerByKey]
  );

  /** Rows without decisions: everything that depends only on the submission. */
  const baseRows = useMemo(() => {
    const out: Omit<ReviewRow, "decision" | "note" | "carried">[] = [];
    const push = (
      step: number,
      stepNode: OutgoingIntakeFormNode,
      q: OutgoingIntakeFormNode,
      key: string,
      label: string,
      context: string | null,
      instanceId: string | null
    ) => {
      const answer = answerByKey.get(key) ?? null;
      const before = previousByKey.get(key) ?? null;
      const isChanged = Boolean(changed?.has(key));
      out.push({
        key,
        step,
        stepNodeId: text(stepNode.node_id),
        instanceId,
        node: q,
        label,
        context,
        answer,
        display: formatGroupAnswerValue(q, answer),
        files: answer?.files ?? [],
        comment: text(answer?.comment) || null,
        changed: isChanged,
        previousDisplay:
          isChanged && previous ? formatGroupAnswerValue(q, before) : null,
        previousFiles: isChanged ? before?.files ?? [] : [],
        attention: attentionReason(answer),
        savesTo: savesToOf(q, targets, label)
      });
    };
    stepNodes.forEach((node, i) => {
      const id = text(node.node_id);
      if (node.node_type === "repeatable_group") {
        (instanceOrder.get(id) ?? []).forEach((instanceId, idx) => {
          const entry = entryAnswersFor(node, instanceId);
          for (const q of groupChildQuestions(node).filter((c) =>
            isNodeVisible(c, valueByQuestionId)
          )) {
            push(
              i + 1,
              node,
              q,
              decisionKey({
                question_id: q.node_id,
                repeatable_group_id: id,
                repeatable_group_instance_id: instanceId
              }),
              reviewLabelFor(q, node, entry),
              `${groupQuestionTitle(node) || text(node.title) || "Entry"} ${
                idx + 1
              }`,
              instanceId
            );
          }
        });
        return;
      }
      push(
        i + 1,
        node,
        node,
        decisionKey({ question_id: id }),
        reviewLabelFor(node),
        null,
        null
      );
    });
    return out;
  }, [
    answerByKey,
    changed,
    entryAnswersFor,
    instanceOrder,
    previous,
    previousByKey,
    stepNodes,
    targets,
    valueByQuestionId
  ]);
  const reviewKeys = useMemo(() => baseRows.map((r) => r.key), [baseRows]);

  // ---- decisions ------------------------------------------------------------
  const [byKey, setByKey] = useState<Record<string, DecisionState>>({});
  const dirty = useRef(false);
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!submission || !reviewKeys.length) return;
    const seedKey = `${submissionId}:${submission.decisions?.length ?? 0}:${
      submission.review_status
    }:${reviewKeys.length}:${previous?.outgoing_intake_submission_id ?? ""}`;
    if (seeded.current === seedKey) return;
    seeded.current = seedKey;
    dirty.current = false;
    setByKey(seedDecisions(reviewKeys, submission, previous));
  }, [previous, reviewKeys, submission, submissionId]);

  const setDecision = useCallback((key: string, status: Decision) => {
    dirty.current = true;
    setByKey((prev) => {
      const existing = prev[key] ?? { status: "pending", note: "" };
      if (status === "accepted")
        return { ...prev, [key]: { status: "accepted", note: "" } };
      return { ...prev, [key]: { status, note: existing.note ?? "" } };
    });
  }, []);
  const setNote = useCallback((key: string, note: string) => {
    dirty.current = true;
    setByKey((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { status: "pending" }),
        status: "rejected",
        note
      }
    }));
  }, []);
  const acceptKeys = useCallback((keys: string[]) => {
    if (!keys.length) return;
    dirty.current = true;
    setByKey((prev) => {
      const next = { ...prev };
      for (const k of keys) next[k] = { status: "accepted", note: "" };
      return next;
    });
  }, []);

  const rows: ReviewRow[] = useMemo(
    () =>
      baseRows.map((r) => {
        const st = byKey[r.key];
        return {
          ...r,
          decision: st?.status ?? "pending",
          note: st?.note ?? "",
          carried: Boolean(st?.carried)
        };
      }),
    [baseRows, byKey]
  );

  const decisions = useMemo((): OutgoingIntakeReviewDecision[] => {
    return Object.entries(byKey).flatMap(([key, st]) => {
      if (st.status === "pending") return [];
      const [question_id, repeatable_group_id, repeatable_group_instance_id] =
        key.split("|");
      return [
        {
          question_id,
          repeatable_group_id: repeatable_group_id || null,
          repeatable_group_instance_id: repeatable_group_instance_id || null,
          status: st.status,
          note: st.status === "rejected" ? text(st.note) || null : null
        }
      ];
    });
  }, [byKey]);

  const counts = useMemo(() => {
    let acceptedN = 0;
    let rejected = 0;
    let pending = 0;
    for (const r of rows) {
      if (r.decision === "accepted") acceptedN += 1;
      else if (r.decision === "rejected") rejected += 1;
      else pending += 1;
    }
    return {
      accepted: acceptedN,
      rejected,
      pending,
      total: rows.length,
      steps: stepNodes.length,
      changed: rows.filter((r) => r.changed).length,
      attention: rows.filter((r) => r.attention !== null).length,
      carried: rows.filter((r) => r.carried).length
    };
  }, [rows, stepNodes.length]);

  const safeKeys = useMemo(() => safeToAccept(rows), [rows]);
  const filterCounts = useMemo(() => {
    const out = {} as Record<ReviewFilter, number>;
    for (const f of REVIEW_FILTERS)
      out[f.id] = rows.filter((r) => matchesFilter(r, f.id)).length;
    return out;
  }, [rows]);

  // ---- autosave -------------------------------------------------------------
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const save = useCallback(
    async (payload: OutgoingIntakeReviewDecision[]) => {
      setSaveState("saving");
      try {
        await OutgoingIntakesAPI.reviewSubmission(
          clientId,
          intakeId,
          submissionId,
          { decisions: payload }
        );
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    },
    [clientId, intakeId, submissionId]
  );
  useEffect(() => {
    if (locked || !submission || !dirty.current) return undefined;
    const handle = window.setTimeout(() => void save(decisions), 800);
    return () => window.clearTimeout(handle);
  }, [decisions, locked, save, submission]);
  const retrySave = useCallback(() => void save(decisions), [decisions, save]);

  // ---- what the client view needs -------------------------------------------
  const reviewDecisionByKey = useMemo(() => {
    const out: Record<string, Decision> = {};
    for (const [k, st] of Object.entries(byKey)) out[k] = st.status;
    return out;
  }, [byKey]);
  const reviewNoteByKey = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, st] of Object.entries(byKey)) out[k] = st.note ?? "";
    return out;
  }, [byKey]);
  const reviewCommentsByKey = useMemo(() => {
    const out: Record<string, ReviewCommentThreadItem[]> = {};
    const attorney = text(reviewerName) || "Reviewer";
    const client = text(clientName) || "Client";
    for (const key of reviewKeys) {
      const thread: ReviewCommentThreadItem[] = [];
      submissions.forEach((s, idx) => {
        const ans = (s.answers ?? []).find((a) => decisionKey(a) === key);
        const comment = text(ans?.comment);
        if (comment) {
          const at = s.submitted_at ?? s.created_at ?? null;
          thread.push({
            author: "client",
            name: client,
            text: comment,
            at,
            revisionNumber: idx + 1,
            timeLabel: shortTime(at)
          });
        }
        const d = (s.decisions ?? []).find((x) => decisionKey(x) === key);
        const note = text(d?.note);
        const editable =
          s.outgoing_intake_submission_id === submissionId && !locked;
        if (d?.status === "rejected" && note && !editable) {
          const at = s.reviewed_at ?? s.updated_at ?? null;
          thread.push({
            author: "attorney",
            name: attorney,
            text: note,
            at,
            revisionNumber: idx + 1,
            avatarUrl: normalizeAvatarSrc(reviewerAvatar) || null,
            timeLabel: shortTime(at)
          });
        }
      });
      if (thread.length) out[key] = thread;
    }
    return out;
  }, [
    clientName,
    locked,
    reviewKeys,
    reviewerAvatar,
    reviewerName,
    submissionId,
    submissions
  ]);

  // ---- steps, for the client view ------------------------------------------
  const statusOf = useCallback(
    (keys: string[]): StepDecision => {
      const statuses = keys.map((k) => byKey[k]?.status ?? "pending");
      if (statuses.some((s) => s === "rejected")) return "rejected";
      if (statuses.length && statuses.every((s) => s === "accepted"))
        return "accepted";
      if (statuses.some((s) => s === "accepted")) return "partial";
      return "pending";
    },
    [byKey]
  );
  const steps = useMemo<ReviewStep[]>(
    () =>
      stepNodes.map((node, i) => {
        const id = text(node.node_id);
        const keys = baseRows
          .filter((r) => r.stepNodeId === id)
          .map((r) => r.key);
        const entries = (instanceOrder.get(id) ?? []).map((instanceId, idx) => {
          const entryKeys = baseRows
            .filter((r) => r.stepNodeId === id && r.instanceId === instanceId)
            .map((r) => r.key);
          return {
            instanceId,
            label: `${text(node.title) || "Entry"} ${idx + 1}`,
            keys: entryKeys,
            decision: statusOf(entryKeys)
          };
        });
        return {
          node,
          number: i + 1,
          label:
            node.node_type === "repeatable_group"
              ? groupQuestionTitle(node)
              : reviewLabelFor(node),
          keys,
          decision: statusOf(keys),
          entries: node.node_type === "repeatable_group" ? entries : []
        };
      }),
    [baseRows, instanceOrder, statusOf, stepNodes]
  );

  // ---- revision request -----------------------------------------------------
  const clientFirstName = firstName(clientName);
  const flaggedRows = useMemo(
    () => rows.filter((r) => r.decision === "rejected"),
    [rows]
  );
  const draftNote = useMemo(
    () =>
      draftRevisionNote(
        clientFirstName,
        flaggedRows.map((r) => ({ label: r.label, note: r.note }))
      ),
    [clientFirstName, flaggedRows]
  );
  const [customNote, setCustomNote] = useState<string | null>(null);
  useEffect(() => setCustomNote(null), [submissionId]);
  const revisionNote = customNote ?? draftNote;

  const [busy, setBusy] = useState<null | "revision" | "map">(null);

  const refresh = useCallback(async () => {
    await data.loadDetail(intakeId, { force: true });
    invalidate(["intakes", "submissions", "activity"]);
  }, [data, intakeId, invalidate]);

  const requestRevision = useCallback(async (): Promise<boolean> => {
    if (!submission || counts.rejected === 0) return false;
    setBusy("revision");
    try {
      const res = await OutgoingIntakesAPI.requestRevision(
        clientId,
        intakeId,
        submissionId,
        { decisions, revision_request_note: text(revisionNote) || null }
      );
      if (res?.email_delivered === false) toastEmailFailed(clientId, intakeId);
      else
        toast.success("Sent back for changes", {
          description: `${
            clientFirstName ?? "Your client"
          } was emailed. Her current link keeps working.`
        });
      await refresh();
      return true;
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Couldn't request the changes"
      );
      return false;
    } finally {
      setBusy(null);
    }
  }, [
    clientFirstName,
    clientId,
    counts.rejected,
    decisions,
    intakeId,
    refresh,
    revisionNote,
    submission,
    submissionId
  ]);

  // ---- mapping an answer to a profile field ----------------------------------
  const mapAnswer = useCallback(
    async (nodeId: string, modelPath: string | null) => {
      if (!intake) return;
      setBusy("map");
      try {
        const next = updateNode(intake.schema ?? [], nodeId, {
          model_path: modelPath
        });
        await OutgoingIntakesAPI.patch(clientId, intakeId, {
          form_schema: next
        });
        await refresh();
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Couldn't save the mapping"
        );
      } finally {
        setBusy(null);
      }
    },
    [clientId, intake, intakeId, refresh]
  );

  return {
    intake,
    submission,
    previous,
    submissions,
    submissionNumber,
    latest,
    superseded,
    accepted,
    schema,
    locked,
    loading: !detail,
    steps,
    rows,
    reviewKeys,
    counts,
    filterCounts,
    safeKeys,
    byKey,
    setDecision,
    setNote,
    acceptKeys,
    decisions,
    saveState,
    retrySave,
    reviewDecisionByKey,
    reviewNoteByKey,
    reviewCommentsByKey,
    clientFirstName,
    flaggedRows,
    revisionNote,
    setRevisionNote: setCustomNote,
    resetRevisionNote: () => setCustomNote(null),
    busy,
    requestRevision,
    mapAnswer,
    refresh,
    openIntake: () =>
      intake && open({ kind: "intake", id: intakeId, title: intake.title }),
    openSubmission: (s: OutgoingIntakeSubmission) =>
      open({
        kind: "submission",
        id: `${intakeId}/${s.outgoing_intake_submission_id}`,
        title: `${intake?.title ?? "Intake"} · Submission #${
          submissions.indexOf(s) + 1
        }`
      })
  };
};

export type SubmissionReview = ReturnType<typeof useSubmissionReview>;
